import type { Field } from '@formily/core';
import { useField } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { Select } from 'antd';
import { useState, useMemo, useEffect } from 'react';
import { uniqBy } from 'lodash';
import styled, { createGlobalStyle } from 'styled-components';

import { usePage } from '../../context/PageContext';
import { IBaseTableParams, ReferenceType } from '../../../types';
import { useDebounce } from '../../../hooks/useDebounce';
import type { IFormReferenceSelectProps, IReferenceValue } from './helper';
import {
  buildFiltersParam,
  buildSearchJsonLogic,
} from '../../utils/filterHelpers';
import { getFormContext } from '../../utils/formContext';
import { buildSorter } from '../../utils/buildSorter';
import useFetchModelInstancesCursorPagination from '../../hooks/api/useFetchModelInstancesCursorPagination';
import useFetchOrganizations from '../../hooks/api/useFetchOrganizations';
import useFetchUsers from '../../hooks/api/useFetchUsers';
import useFetchOrganizationUsers from '../../hooks/api/useFetchOrganizationUsers';
import { transformToReferenceValue } from './utils';
import { useLazyLoad } from '../../../hooks';
import { encodeUrlParams } from '../../utils/urlUtils';
import { useFetchLoadableData } from '../loadable-data/useFetchLoadableData';
import { searchJmesPath } from '../../../utils';
import { useFormilyValuesChangeIndex } from '../../hooks/useFormilyValuesChangeIndex';

interface ISelectOption {
  label: string;
  value: string | number;
  version?: number;
  modelId?: string;
  // Optional secondary line shown under the label in the dropdown. Produced by
  // the transformer (e.g. `subLabel: data.group`), rendered only when present.
  subLabel?: string;
}

// Fields the BE adds when it enriches a reference (see docs/references.md).
// Their presence means the value is still the full enriched object and must be
// transformed down to the clean reference shape before it can be submitted.
// `transformToReferenceValue` never emits these, so this can't cause a
// re-transform loop.
const ENRICHED_REFERENCE_MARKERS = ['data', 'modelVersionId', 'updatedAt'];

const isEnrichedReference = (value: any): boolean =>
  !!value &&
  typeof value === 'object' &&
  ENRICHED_REFERENCE_MARKERS.some(key => key in value);

const shouldBuildInitialValue = (initialValue: any, mode?: string) => {
  if (!initialValue) return false;

  const isSingleMode = !['multiple', 'tags'].includes(mode || 'default');

  if (isSingleMode) return isEnrichedReference(initialValue);

  if (Array.isArray(initialValue)) {
    return initialValue.some(isEnrichedReference);
  }

  return false;
};

// const shouldBuildInitialValueOld = (initialValue: any, mode?: string) => {
//   const isSingleMode = !['multiple', 'tags'].includes(mode || 'default');

//   if (isSingleMode && (!initialValue || !initialValue.id)) return false;

//   if (!isSingleMode && Array.isArray(initialValue)) {
//     return initialValue.some((v: IReferenceValue) => !v._formDisplayValue);
//   }

//   return !initialValue?._formDisplayValue;
// };

const FormReferenceSelect = observer(() => {
  const field = useField<Field>();

  const changeIndex = useFormilyValuesChangeIndex(field.form);

  const page = usePage();

  const componentProps = (field.componentProps ||
    {}) as IFormReferenceSelectProps;
  const {
    referenceType,
    modelIds,
    organizationId,
    searchableFields,
    transformer,
    placeholder,
    disabled: disabledProp,
    allowClear,
    mode,
    size,
    internalReferencePath,
    modelInstanceFilters: modelInstanceFiltersProps,
    numberToLoadOptions,
    sortName,
    sortOrder,
    url,
    wrapText,
    transformerToInstances,
    ...rest
  } = componentProps;
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearch = useDebounce(searchValue, 500);

  const disabled = disabledProp || field.disabled;

  const isMultiple = mode === 'multiple' || mode === 'tags';

  // Cache the items originally loaded from the BE so that, when one is removed
  // from a multi-select, we can send it back flagged `toBeDeleted` instead of
  // silently dropping it (the BE ignores omitted array items).
  const [originalItems] = useState<IReferenceValue[]>(() => {
    const initial = field.value ?? field.initialValue;
    return Array.isArray(initial) ? (initial as IReferenceValue[]) : [];
  });

  const [localPage, setLocalPage] = useState(1);

  // Convert current field value back to Select value format
  const selectValue = useMemo(() => {
    const fieldValue = field.value;
    if (!fieldValue) {
      return undefined;
    }

    if (Array.isArray(fieldValue)) {
      return fieldValue
        .filter((v: IReferenceValue) => !v.toBeDeleted)
        .map((v: IReferenceValue) => v.id);
    }

    if (typeof fieldValue === 'object' && 'id' in fieldValue) {
      return (fieldValue as IReferenceValue).id;
    }

    return undefined;
  }, [field.value]);

  // Actual selected ids (empty when nothing is selected). An empty multi-select
  // yields `[]`, which is truthy — so we must guard the "active instances" fetch
  // on real selections, otherwise it fetches with `modelInstanceIds: []` (the BE
  // treats that as no filter → returns everything) and pollutes the filtered
  // option list.
  const selectedIds = useMemo(
    () =>
      Array.isArray(selectValue)
        ? selectValue
        : selectValue
          ? [selectValue]
          : [],
    [selectValue]
  );
  const hasSelection = selectedIds.length > 0;

  /**
   * The initial value from the BE is the fully-enriched reference object
   * (`{ id, modelId, modelVersionId, data, version, updatedAt }`). We must strip
   * it down to the clean reference shape so it isn't submitted verbatim (the BE
   * rejects unknown paths like `…data.rowid`).
   *
   * Reference data often arrives asynchronously, so this must react to
   * `field.initialValue` changing — not just run on mount — otherwise a value
   * that lands after mount is never cleaned. `shouldBuildInitialValue` returns
   * false once the value already carries `_formDisplayValue`, which prevents a
   * re-transform loop and avoids clobbering user selections.
   */
  useEffect(() => {
    if (shouldBuildInitialValue(field.initialValue, mode)) {
      const transformedValue = transformToReferenceValue(
        field.initialValue,
        (field.componentProps as IFormReferenceSelectProps)?.transformer
      );
      field.setInitialValue(transformedValue);
      field.setValue(transformedValue);
    }
  }, [field.initialValue, mode]);

  // Fetch with fixed url
  const { loadableData, isLoadingLoadableData } = useFetchLoadableData(
    encodeUrlParams(url || ''),
    {
      enabled: !!url,
    }
  );

  // Fetch users
  const { users, isLoadingUsers } = useFetchUsers({
    params: {
      search: debouncedSearch,
      page: localPage,
    },
    options: {
      enabled: !url && referenceType === ReferenceType.USER && !organizationId,
    },
  });

  // Fetch organization users
  const { organizationUsers, isLoadingOrganizationUsers } =
    useFetchOrganizationUsers(
      organizationId!,
      {
        search: debouncedSearch,
        page: localPage,
      } as IBaseTableParams,
      {
        enabled:
          !url &&
          referenceType === ReferenceType.USER &&
          Boolean(organizationId),
      }
    );

  // Fetch organizations
  const { organizations, isLoadingOrganizations } = useFetchOrganizations({
    params: {
      search: debouncedSearch,
      page: localPage,
    },
    options: {
      enabled: !url && referenceType === ReferenceType.ORGANIZATION,
    },
  });

  const formContext = useMemo(() => getFormContext(field.form), [field.form]);

  // Build filters for Model Instance based on searchableFields
  const modelInstanceFilters = useMemo(() => {
    const shouldSearch =
      referenceType === ReferenceType.MODEL_INSTANCE &&
      searchableFields &&
      searchableFields.length > 0 &&
      debouncedSearch;

    return buildFiltersParam({
      staticFilters: [modelInstanceFiltersProps],
      extraFilters: [
        shouldSearch && buildSearchJsonLogic(searchableFields, debouncedSearch),
      ],
      context: formContext,
      syntax: 'runtime',
    });
  }, [
    referenceType,
    searchableFields,
    debouncedSearch,
    modelInstanceFiltersProps,
    formContext,
  ]);

  // Fetch model instances
  const { modelInstances, isLoadingModelInstances } =
    useFetchModelInstancesCursorPagination({
      params: {
        modelIds,
        filters: modelInstanceFilters,
        size: numberToLoadOptions || 10,
        page: localPage,
        sort: buildSorter({ sortName, sortOrder }),
      },
      options: {
        enabled:
          !url &&
          referenceType === ReferenceType.MODEL_INSTANCE &&
          modelIds &&
          modelIds.length > 0,
        placeholderData: previousData => previousData,
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    });
  const {
    modelInstances: activeModelInstances,
    isLoadingModelInstances: isLoadingActiveModelInstances,
  } = useFetchModelInstancesCursorPagination({
    params: {
      modelIds,
      modelInstanceIds: hasSelection ? selectedIds : undefined,
    },
    options: {
      enabled:
        !url &&
        referenceType === ReferenceType.MODEL_INSTANCE &&
        modelIds &&
        modelIds.length > 0 &&
        hasSelection,
    },
  });

  const internalReferences = useMemo(() => {
    if (
      referenceType !== ReferenceType.INTERNAL_REFERENCE ||
      !internalReferencePath
    ) {
      return [];
    }

    let rootForm = field.form as any; // TODO: fix this types
    const parentValues: any[] = [];
    while (rootForm.props.parent) {
      rootForm = rootForm.props.parent;
      parentValues.push(rootForm.values);
    }
    let parentForms: any = {};
    for (let i = parentValues.length - 1; i >= 0; i--) {
      parentForms = { ...parentValues[i], parent: parentForms };
    }
    const mainFormValues = rootForm.values;
    const references = searchJmesPath(
      {
        form: field.form.values,
        mainForm: mainFormValues,
        context: page.context,
        parent: parentForms,
      },
      internalReferencePath
    );

    const safeReferences = Array.isArray(references) ? references : [];

    return safeReferences.filter(ref => !ref.toBeDeleted);
  }, [
    internalReferencePath,
    referenceType,
    page,
    field.form,
    field.form.values,
    changeIndex,
  ]);
  // Determine which data to use based on reference type
  const rawData = useMemo(() => {
    if (url) {
      if (!transformerToInstances) {
        return loadableData;
      }
      const instances = searchJmesPath(loadableData, transformerToInstances);
      return Array.isArray(instances) ? instances : [];
    }

    switch (referenceType) {
      case ReferenceType.USER:
        return organizationId ? organizationUsers?.data : users?.data;
      case ReferenceType.ORGANIZATION:
        return organizations?.data;
      case ReferenceType.MODEL_INSTANCE:
        return modelInstances?.data;
      case ReferenceType.INTERNAL_REFERENCE:
        return internalReferences;
      default:
        return null;
    }
  }, [
    url,
    loadableData,
    transformerToInstances,
    referenceType,
    users,
    organizations,
    modelInstances,
    organizationUsers,
    internalReferences,
  ]);

  const hasNext = useMemo(() => {
    if (url) {
      return false;
    }

    switch (referenceType) {
      case ReferenceType.USER:
        return organizationId
          ? organizationUsers &&
              organizationUsers?.meta?.pages > organizationUsers?.meta?.page
          : users && users?.meta?.pages > users?.meta?.total;
      case ReferenceType.ORGANIZATION:
        return (
          organizations && organizations?.meta.pages > organizations?.meta.page
        );
      case ReferenceType.MODEL_INSTANCE:
        return modelInstances?.hasNext;
      case ReferenceType.INTERNAL_REFERENCE:
        return false;
      default:
        return false;
    }
  }, [referenceType, users, organizations, modelInstances, organizationUsers]);

  // Lazy load is disabled if all is fetched on FE
  const isLazyLoadEnabled = useMemo(() => {
    if (url || referenceType === ReferenceType.INTERNAL_REFERENCE) return false;
    return true;
  }, [referenceType, url]);

  const lazyLoadResetKey = useMemo(() => {
    if (referenceType === ReferenceType.MODEL_INSTANCE) {
      return JSON.stringify({ modelInstanceFilters });
    }

    return debouncedSearch;
  }, [modelInstanceFilters, debouncedSearch, referenceType]);

  useEffect(() => {
    if (localPage !== 1) {
      setLocalPage(1);
    }
  }, [lazyLoadResetKey]);

  // Determine loading state
  const isLoading =
    isLoadingUsers ||
    isLoadingOrganizations ||
    isLoadingModelInstances ||
    isLoadingOrganizationUsers ||
    isLoadingActiveModelInstances ||
    isLoadingLoadableData;

  const { data: accumulatedValues, onPopupScroll } = useLazyLoad({
    pageData: rawData,
    hasMore: hasNext ?? false,
    isLoading: isLoading,
    resetKey: lazyLoadResetKey,
    isEnabled: isLazyLoadEnabled,
    onLoadMore: () => setLocalPage(p => p + 1),
  });

  const selectedData = useMemo(() => {
    if (!hasSelection) return [];

    if (url) {
      return [];
    }

    switch (referenceType) {
      case ReferenceType.USER:
        return []; // TODO
      case ReferenceType.ORGANIZATION:
        return []; //TODO
      case ReferenceType.MODEL_INSTANCE:
        return activeModelInstances?.data;
      case ReferenceType.INTERNAL_REFERENCE:
        return [];
      default:
        return null;
    }
  }, [referenceType, activeModelInstances, hasSelection]);

  const data = useMemo(() => {
    if (!isLazyLoadEnabled) {
      // if lazy load is disabled return raw data, no search check needed, no accumulation needed
      return rawData;
    }

    if (searchValue) {
      // If searchValue exists skip showing active model instances
      return accumulatedValues;
    }
    const data = [...(accumulatedValues || []), ...(selectedData || [])].filter(
      v => !!v
    );

    return uniqBy(data, v => v.id);
  }, [
    searchValue,
    selectedData,
    accumulatedValues,
    isLazyLoadEnabled,
    rawData,
  ]);

  // Transform data using JMESPath transformer
  const options = useMemo(() => {
    // If transformer is not set return undefined imidiately
    if (!data || !transformer) {
      return undefined;
    }

    try {
      const transformed = searchJmesPath(data, transformer);
      if (!Array.isArray(transformed)) {
        console.error('Transformer must return an array');
        return [];
      }
      return transformed;
    } catch (error) {
      console.error('Error transforming data', error);
      return [];
    }
  }, [data, transformer]);

  // Handle value change - convert to IReferenceValue format
  const handleChange = (
    value: string | string[] | undefined,
    option: ISelectOption[] | ISelectOption | undefined
  ) => {
    if (isMultiple) {
      // Multiple mode. `value` is the list of currently selected ids (antd
      // passes undefined when everything is cleared).
      const selectedIds = Array.isArray(value) ? value : [];
      const referenceValues: IReferenceValue[] = selectedIds.map(v => {
        const selectedOption = (options as ISelectOption[])?.find(
          (o: ISelectOption) => o.value === v
        );

        const original = originalItems.find(o => o.id === v);

        return {
          id: v,
          version: selectedOption ? selectedOption.version : original?.version,
          modelId: selectedOption ? selectedOption.modelId : original?.modelId,
          _formDisplayValue:
            selectedOption?.label || original?._formDisplayValue || String(v),
          _meta: selectedOption,
        };
      });

      // Any originally-persisted item that is no longer selected is kept with
      // `toBeDeleted: true` so the BE removes it from the array.
      originalItems.forEach(original => {
        if (!selectedIds.includes(original.id)) {
          referenceValues.push({ ...original, toBeDeleted: true });
        }
      });
      field.setValue(referenceValues);
      field.validate();
      return;
    }

    if (!value || Array.isArray(value)) {
      field.setValue(null);
      return;
    }

    // Single mode
    const referenceValue: IReferenceValue = {
      id: value,
      version: (option as ISelectOption).version,
      modelId: (option as ISelectOption).modelId,
      _formDisplayValue: (option as ISelectOption).label || String(value),
      _meta: option,
    };
    field.setValue(referenceValue);
    field.validate();
  };

  // Sync options and dataSource in form field state
  // We need this so we can get options into reactions, no need for this in component, just in reactions
  useEffect(() => {
    if (options) {
      field.setState({
        dataSource: options,
      });
    }
  }, [options]);

  const optionsOrDataSource = options || field.getState().dataSource || [];

  // Whether any option carries a `subLabel` (secondary line). When it does we
  // disable virtual scrolling so the variable-height options render correctly.
  const hasSubLabel = useMemo(
    () => (optionsOrDataSource as ISelectOption[]).some(o => o?.subLabel),
    [optionsOrDataSource]
  );

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setLocalPage(1);
  };

  return (
    <SelectWrap className={wrapText ? 'wrap-text' : undefined}>
      {wrapText && <WrapPopupGlobalStyle />}
      <Select
        {...rest}
        value={selectValue}
        onChange={(value, option) =>
          handleChange(
            value,
            option as ISelectOption[] | ISelectOption | undefined
          )
        }
        onSearch={isLazyLoadEnabled ? handleSearch : undefined}
        searchValue={isLazyLoadEnabled ? searchValue : undefined}
        placeholder={placeholder || 'Select...'}
        disabled={disabled}
        allowClear={allowClear}
        mode={mode}
        size={size}
        loading={isLoading}
        options={optionsOrDataSource}
        showSearch={true}
        filterOption={
          isLazyLoadEnabled
            ? false
            : (input, option) =>
                ((option?.label ?? '') as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
        }
        onPopupScroll={isLazyLoadEnabled ? onPopupScroll : undefined}
        // The dropdown is portaled to the body; wrap its option text via a popup
        // class (styled globally below). Disable virtual scrolling so the list
        // measures variable-height (wrapped / sub-labelled) options correctly
        // and still scrolls within its max-height.
        popupClassName={wrapText ? WRAP_POPUP_CLASS : undefined}
        virtual={wrapText || hasSubLabel ? false : undefined}
        // Render an optional secondary line under the label when the transformer
        // produced a `subLabel`. The selected value/tags still show just `label`.
        optionRender={
          hasSubLabel
            ? (oriOption: any) => {
                const data = (oriOption?.data ?? {}) as ISelectOption;
                if (!data.subLabel) return oriOption?.label ?? data.label;
                return (
                  <OptionContent>
                    <span className="opt-label">{data.label}</span>
                    <span className="opt-sub">{data.subLabel}</span>
                  </OptionContent>
                );
              }
            : undefined
        }
      />
    </SelectWrap>
  );
});

export default FormReferenceSelect;

// When `wrap-text` is enabled, override antd's default truncation so long
// labels wrap to full text — both for the selected value/tags and the dropdown
// options, in single and multiple mode.
const SelectWrap = styled.div`
  width: 100%;

  .ant-select {
    width: 100%;
  }

  &.wrap-text.ant-select-single,
  &.wrap-text .ant-select-single {
    height: auto;
    display: block;
  }

  &.wrap-text .ant-select-selector {
    height: auto;
    min-height: 38px;
    align-items: center;
  }

  &.wrap-text .ant-select-selection-item {
    white-space: normal;
    word-break: break-word;
    height: auto;
    line-height: 1.5;
    max-width: 100%;
  }

  &.wrap-text .ant-select-selection-item-content {
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    text-overflow: clip;
    min-height: 30px;
    display: flex;
    align-items: center;
  }
`;

// The dropdown is rendered in a body portal (outside SelectWrap), so its option
// text must be wrapped via a global rule scoped to the popup class.
const WRAP_POPUP_CLASS = 'ref-select-wrap-popup';

const WrapPopupGlobalStyle = createGlobalStyle`
  .${WRAP_POPUP_CLASS} .ant-select-item-option-content {
    white-space: normal;
    word-break: break-word;
  }
`;

// Two-line option: label on top, optional muted sub-text below.
const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.35;

  .opt-sub {
    font-size: 12px;
    color: ${({ theme }) => theme?.colors?.textSecondary || '#8c8c8c'};
  }
`;
